import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import { Button, IconButton, Stack, Typography, useMediaQuery } from "@mui/material";
import { Outlet } from "react-router";
import AppLayout from "@/components/AppLayout";
import Block from "@/components/Block";
import TextButton from "@/components/TextButton";
import { useAppConfig } from "@/contexts/AppConfigContext";
import { useBookingContext } from "@/contexts/BookingContext";
import { STEPS, useStepNavigation } from "@/hooks/useStepNavigation";
import { primaryActionSx, theme } from "@/theme/mui-theme";
import MobileSearchAccordion from "./MobileSearchAccordion";
import SearchProgressIndicator from "./SearchProgressIndicator";

export default function SearchLayout() {
  const { i18n } = useLingui();
  const {
    currentStep,
    isStepAvailable,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    nextStep,
    previousStep,
  } = useStepNavigation();
  const { actions } = useBookingContext();
  const { affiliateLabel } = useAppConfig();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const handleReset = () => {
    actions.resetBooking();
    goToStep("dates");
  };

  const stepLayout = isMobile ? (
    <MobileSearchAccordion />
  ) : (
    <>
      <SearchProgressIndicator />
      <Block
        sx={{ p: 0, height: 412, overflowY: "auto", display: "flex", flexDirection: "column" }}
        elevation={10}
      >
        <Typography variant="h2" sx={{ mx: 2, mt: 3, mb: 2 }}>
          {i18n._(STEPS[currentStep].title)}
        </Typography>
        <Outlet />
      </Block>
    </>
  );

  return (
    <AppLayout contentMaxWidth="sm" centerContent>
      <Stack
        spacing={{ xs: 1, md: 2 }}
        sx={{
          p: 2,
          py: { xs: 2, md: 0.5 },
          pb: { xs: 8, md: 0 },
          height: { xs: `calc(100dvh - ${affiliateLabel ? "120px" : "96px"})`, md: "auto" },
          overflowY: { xs: "clip", md: "visible" },
        }}
      >
        {stepLayout}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{
            position: { xs: "fixed", md: "static" },
            bottom: { xs: 0, md: "auto" },
            left: { xs: 0, md: "auto" },
            right: { xs: 0, md: "auto" },
            pl: 2,
            pr: { xs: 2, md: 0 },
            py: { xs: 2, md: 0 },
            bgcolor: "transparent",
            zIndex: { xs: "appBar", md: "auto" },
          }}
        >
          {isMobile ? (
            previousStep && (
              <IconButton
                onClick={goToPreviousStep}
                sx={{ border: `1px solid ${theme.palette.divider}`, width: 60, height: 60 }}
              >
                <ArrowBackIosNewIcon sx={{ color: "text.primary" }} />
              </IconButton>
            )
          ) : (
            <TextButton onClick={handleReset} sx={{ color: "text.primary" }}>
              <Trans>Reset</Trans>
            </TextButton>
          )}
          <Button
            variant="contained"
            size="large"
            onClick={goToNextStep}
            disabled={!isStepAvailable(nextStep)}
            sx={(theme) => ({
              width: { xs: "100%", md: "210px" },
              height: 60,
              ...(nextStep === "results" ? primaryActionSx(theme) : {}),
            })}
            startIcon={nextStep === "results" ? <SearchIcon /> : null}
          >
            <Trans>{i18n._(STEPS[currentStep].nextButtonLabel as MessageDescriptor)}</Trans>
          </Button>
        </Stack>
      </Stack>
    </AppLayout>
  );
}
